import React, { useEffect, useRef, useState } from "react";
import API_URL from "../../config/config";

const CONSENT_KEY = "instaport_blog_analytics_consent";
const COOKIE_NAME = "instaport_blog_visitor";
const uuid = () => {
  if (window.crypto.randomUUID) return window.crypto.randomUUID();
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join(
    "",
  );
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(
    12,
    16,
  )}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};
const readConsent = () => {
  if (navigator.globalPrivacyControl || navigator.doNotTrack === "1")
    return "declined";
  try {
    return localStorage.getItem(CONSENT_KEY) || "pending";
  } catch {
    return "pending";
  }
};
const visitorId = (consent) => {
  if (consent !== "accepted") {
    document.cookie = `${COOKIE_NAME}=; Max-Age=0; Path=/; SameSite=Lax`;
    return "";
  }
  const existing = document.cookie
    .split("; ")
    .find((v) => v.startsWith(`${COOKIE_NAME}=`))
    ?.split("=")[1];
  if (existing && /^[a-f\d-]{36}$/i.test(existing)) return existing;
  const id = uuid();
  document.cookie = `${COOKIE_NAME}=${id}; Max-Age=15552000; Path=/; SameSite=Lax${
    window.location.protocol === "https:" ? "; Secure" : ""
  }`;
  return id;
};
export default function BlogViewTracker({ blogId }) {
  const [consent, setConsent] = useState(readConsent),
    [open, setOpen] = useState(false);
  const consentRef = useRef(consent);
  consentRef.current = consent;
  const flushRef = useRef(null);
  useEffect(() => {
    // In-memory session only until analytics is accepted. StrictMode cleanup cancels the first start.
    const eventId = uuid(),
      secret = uuid();
    let sequence = 0,
      wasVisible = document.visibilityState === "visible";
    let activeSeconds = 0,
      lastTick = performance.now(),
      maxScroll = 0,
      started = false;
    const tick = () => {
      const now = performance.now();
      if (wasVisible) activeSeconds += Math.min(20, (now - lastTick) / 1000);
      lastTick = now;
      wasVisible = document.visibilityState === "visible";
      const article = document.getElementById("journal-article");
      if (article) {
        const r = article.getBoundingClientRect();
        maxScroll = Math.max(
          maxScroll,
          Math.min(
            100,
            Math.max(0, ((window.innerHeight - r.top) / r.height) * 100),
          ),
        );
      }
    };
    const send = () => {
      tick();
      const accepted =
        consentRef.current === "accepted" &&
        !navigator.globalPrivacyControl &&
        navigator.doNotTrack !== "1";
      let token = "";
      if (accepted) {
        try {
          token = localStorage.getItem("token") || "";
        } catch {}
      }
      const body = {
        eventId,
        secret,
        sequence: sequence++,
        consent: accepted,
        activeSeconds: Math.floor(activeSeconds),
        scrollPercent: maxScroll,
        ...(accepted
          ? { visitorId: visitorId("accepted"), referrer: document.referrer }
          : {}),
      };
      fetch(`${API_URL}/blogs/${blogId}/views`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: accepted ? "include" : "omit",
        body: JSON.stringify(body),
        keepalive: true,
      }).catch(() => {});
    };
    const start = setTimeout(() => {
      started = true;
      send();
    }, 800);
    const timer = setInterval(() => {
      if (started && document.visibilityState === "visible") send();
    }, 15000);
    const visibility = () => {
      if (started) send();
      lastTick = performance.now();
    };
    const finish = () => {
      if (started) send();
    };
    flushRef.current = () => {
      if (started) send();
    };
    document.addEventListener("visibilitychange", visibility);
    window.addEventListener("pagehide", finish);
    return () => {
      clearTimeout(start);
      clearInterval(timer);
      finish();
      flushRef.current = null;
      document.removeEventListener("visibilitychange", visibility);
      window.removeEventListener("pagehide", finish);
    };
  }, [blogId]);
  const choose = (value) => {
    if (navigator.globalPrivacyControl || navigator.doNotTrack === "1")
      value = "declined";
    consentRef.current = value;
    setConsent(value);
    try {
      localStorage.setItem(CONSENT_KEY, value);
    } catch {}
    visitorId(value);
    setOpen(false);
    flushRef.current?.();
  };
  return (
    <>
      <button
        className="journal-analytics-choice"
        onClick={() => setOpen((v) => !v)}
      >
        Analytics preferences
      </button>
      {(consent === "pending" || open) && (
        <aside
          className="journal-consent"
          aria-label="Blog analytics preferences"
        >
          <strong>A better journal, with your permission</strong>
          <p>
            We count anonymous visits without cookies. Allow analytics to
            measure repeat visits, approximate city/country, device and
            referring website. If signed in, your visit will be linked to your
            customer profile for our team. We never store your IP address in
            blog analytics.
          </p>
          <div>
            <button onClick={() => choose("declined")}>Anonymous only</button>
            <button onClick={() => choose("accepted")}>Allow analytics</button>
          </div>
        </aside>
      )}
    </>
  );
}
